import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardList, Plus, Pencil, EyeOff, Eye, AlertCircle, RefreshCw } from 'lucide-react';
import { intakeAPI } from '../services/api';
import { useToast, useConfirm } from './UIProvider';
import useDialog from '../hooks/useDialog';
import Select from './Select';
import TablePager, { usePagedRows } from './TablePager';
import { BAND_LABEL } from '../utils/vitals';

/**
 * Settings → Intake Questions: the first-visit medical history catalogue.
 *
 * <p>The questions are seeded once and owned here afterwards, so rewording one, adding one or
 * retiring one never needs a deploy. Answers already given snapshot the wording they were asked
 * under, so an edit here cannot rewrite a patient's record.
 */

const BANDS = ['NEWBORN', 'INFANT', 'YOUNG_CHILD', 'CHILD', 'ADOLESCENT', 'ADULT'];

const SECTIONS = [
  { value: 'FAMILY_HISTORY', label: 'Family history' },
  { value: 'PERSONAL_HISTORY', label: 'Personal history' },
  { value: 'BIRTH_HISTORY', label: 'Birth and early history' },
];
const SECTION_LABEL = Object.fromEntries(SECTIONS.map((s) => [s.value, s.label]));

const ANSWER_TYPES = [
  { value: 'YES_NO', label: 'Yes / No' },
  { value: 'CHOICE', label: 'Choose from a list' },
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DATE', label: 'Date' },
];
const ANSWER_TYPE_LABEL = Object.fromEntries(ANSWER_TYPES.map((t) => [t.value, t.label]));

// The MedicalAlert types a "Yes" can raise. Family history is deliberately absent from the useful
// end of this list: a condition in the FAMILY is not the patient having it, and an alert saying so
// would be wrong. Values match the backend MedicalAlert.AlertType enum.
const ALERT_TYPES = [
  { value: '', label: 'No alert' },
  { value: 'ALLERGY_DRUG', label: 'Drug Allergy' },
  { value: 'ALLERGY_FOOD', label: 'Food Allergy' },
  { value: 'ALLERGY_ENVIRONMENTAL', label: 'Environmental Allergy' },
  { value: 'ALLERGY_OTHER', label: 'Other Allergy' },
  { value: 'CHRONIC_ASTHMA', label: 'Asthma' },
  { value: 'CHRONIC_CANCER', label: 'Cancer' },
  { value: 'CHRONIC_DIABETES', label: 'Diabetes' },
  { value: 'CHRONIC_HEART', label: 'Heart Disease' },
  { value: 'CHRONIC_HYPERTENSION', label: 'Hypertension' },
  { value: 'CHRONIC_KIDNEY', label: 'Kidney Disease' },
  { value: 'CHRONIC_LIVER', label: 'Liver Disease' },
  { value: 'CHRONIC_THYROID', label: 'Thyroid Disorder' },
  { value: 'CHRONIC_OTHER', label: 'Other Chronic Condition' },
  { value: 'SURGICAL_HISTORY', label: 'Surgical History' },
  { value: 'PREGNANCY', label: 'Pregnancy' },
  { value: 'LACTATION', label: 'Lactating' },
  { value: 'OTHER', label: 'Other Alert' },
];
const ALERT_TYPE_LABEL = Object.fromEntries(ALERT_TYPES.map((t) => [t.value, t.label]));

const GENDER_SCOPES = [
  { value: 'ALL', label: 'Everyone' },
  { value: 'FEMALE', label: 'Women only' },
  { value: 'MALE', label: 'Men only' },
];

const INPUT =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500';

const bandsOf = (q) => {
  const raw = (q.ageBands || '').trim();
  return raw ? raw.split(',').map((b) => b.trim()).filter(Boolean) : BANDS;
};

const QuestionFormModal = ({ existing, onClose, onSaved }) => {
  const notify = useToast();
  const [form, setForm] = useState(() => ({
    textUrdu: existing?.textUrdu || '',
    labelEnglish: existing?.labelEnglish || '',
    section: existing?.section || 'FAMILY_HISTORY',
    answerType: existing?.answerType || 'YES_NO',
    choices: existing?.choices || '',
    ageBands: existing ? bandsOf(existing) : [...BANDS],
    genderScope: existing?.genderScope || 'ALL',
    alertType: existing?.alertType || '',
    isSerious: !!existing?.isSerious,
    requiresDetail: !!existing?.requiresDetail,
    isRequired: existing ? !!existing.isRequired : true,
    displayOrder: existing?.displayOrder ?? '',
    isActive: existing ? !!existing.isActive : true,
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const dialogRef = useDialog(() => { if (!saving) onClose(); });

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const toggleBand = (band) =>
    set({
      ageBands: form.ageBands.includes(band)
        ? form.ageBands.filter((b) => b !== band)
        : BANDS.filter((b) => form.ageBands.includes(b) || b === band),
    });

  const submit = async () => {
    if (!form.textUrdu.trim()) { setError('The Urdu question is required.'); return; }
    if (!form.labelEnglish.trim()) { setError('The English label is required.'); return; }
    if (form.ageBands.length === 0) { setError('Pick at least one age group.'); return; }
    if (form.answerType === 'CHOICE' && !form.choices.trim()) {
      setError('List the options, one per line.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,
        displayOrder: form.displayOrder === '' ? null : Number(form.displayOrder),
        alertType: form.alertType || null,
        // All bands selected is stored as "every band", so a band added later is picked up too.
        ageBands: form.ageBands.length === BANDS.length ? [] : form.ageBands,
      };
      if (existing) await intakeAPI.updateQuestion(existing.id, payload);
      else await intakeAPI.createQuestion(payload);
      notify('success', existing ? 'Question updated' : 'Question added');
      onSaved();
    } catch (e) {
      setError(e.message || 'Could not save the question');
      setSaving(false);
    }
  };

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 px-6 py-4 flex-shrink-0">
          <h2 className="text-xl font-bold text-white">
            {existing ? 'Edit Question' : 'Add Question'}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question in Urdu <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.textUrdu}
              onChange={(e) => set({ textUrdu: e.target.value })}
              rows={2}
              maxLength={500}
              dir="rtl"
              className={`${INPUT} urdu-text`}
              style={{ lineHeight: 2.2 }}
              placeholder="کیا خاندان میں کسی کو ذیابیطس ہے؟"
            />
            <p className="text-xs text-gray-500 mt-1">
              What the doctor reads to the patient. Use Western digits in numbers, for example 5 دن.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              English label <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.labelEnglish}
              onChange={(e) => set({ labelEnglish: e.target.value })}
              maxLength={200}
              className={INPUT}
              placeholder="Diabetes in family"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
              <Select
                value={form.section}
                onChange={(v) => set({ section: v })}
                options={SECTIONS}
                buttonClassName="px-3 py-2 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Answer</label>
              <Select
                value={form.answerType}
                onChange={(v) => set({ answerType: v })}
                options={ANSWER_TYPES}
                buttonClassName="px-3 py-2 rounded-lg"
              />
            </div>
          </div>

          {form.answerType === 'CHOICE' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Options, one per line <span className="text-red-500">*</span>
              </label>
              <textarea
                value={form.choices}
                onChange={(e) => set({ choices: e.target.value })}
                rows={4}
                className={INPUT}
                placeholder={'A+\nB+\nO+'}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Asked of which age groups <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {BANDS.map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => toggleBand(b)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors capitalize ${
                    form.ageBands.includes(b)
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {BAND_LABEL[b] || b}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              These are the same age groups the vitals ranges use.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Asked of</label>
            <Select
              value={form.genderScope}
              onChange={(v) => set({ genderScope: v })}
              options={GENDER_SCOPES}
              buttonClassName="px-3 py-2 rounded-lg"
            />
          </div>

          {form.answerType === 'YES_NO' && (
            <div className="p-3 rounded-lg bg-emerald-50/50 border border-emerald-100 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.isSerious}
                  onChange={(e) => set({ isSerious: e.target.checked })}
                  className="rounded border-gray-300 focus:ring-emerald-500"
                />
                <span>A "Yes" is serious, and reads on the Medical History panel</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  A "Yes" also raises this medical alert
                </label>
                <Select
                  value={form.alertType}
                  onChange={(v) => set({ alertType: v })}
                  options={ALERT_TYPES}
                  placeholder="No alert"
                  buttonClassName="px-3 py-2 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The alert goes on the patient's file and shows in the prescription workspace. Leave
                  this at No alert for family history: a condition in the family is not the patient
                  having it.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.isRequired}
                onChange={(e) => set({ isRequired: e.target.checked })}
                className="rounded border-gray-300 focus:ring-emerald-500"
              />
              <span>An answer is required</span>
            </label>
            {form.answerType === 'YES_NO' && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.requiresDetail}
                  onChange={(e) => set({ requiresDetail: e.target.checked })}
                  className="rounded border-gray-300 focus:ring-emerald-500"
                />
                <span>A "Yes" also asks who or what</span>
              </label>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
            <input
              type="number"
              value={form.displayOrder}
              onChange={(e) => set({ displayOrder: e.target.value })}
              className={`${INPUT} sm:w-40`}
              placeholder="Auto"
            />
            <p className="text-xs text-gray-500 mt-1">
              Lower numbers come first. Left blank, a new question goes to the end.
            </p>
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-4 space-y-3">
          {error && (
            <div role="alert" className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
              <span>{saving ? 'Saving...' : 'Save'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const IntakeQuestionsManagement = ({ hasPermission }) => {
  const notify = useToast();
  const confirm = useConfirm();
  const [questions, setQuestions] = useState(null);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState({ open: false, question: null });

  const can = (action) => (typeof hasPermission === 'function' ? hasPermission('SETTINGS_CLINICAL', action) : true);

  const load = useCallback(async () => {
    try {
      const rows = await intakeAPI.listQuestions();
      setQuestions(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Could not load the intake questions');
      setQuestions([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(
    () => (questions || []).filter((q) => showInactive || q.isActive),
    [questions, showInactive]
  );
  const paging = usePagedRows(visible, { id: 'intake-questions', resetOn: [showInactive] });

  const setActive = async (q, active) => {
    if (!active) {
      const ok = await confirm({
        title: `Retire "${q.labelEnglish}"?`,
        message:
          'It stops being asked on new intake forms. Answers already on patients’ files keep it and still read correctly.',
        confirmText: 'Retire',
        tone: 'danger',
      });
      if (!ok) return;
    }
    try {
      await intakeAPI.updateQuestion(q.id, { ...q, ageBands: bandsOf(q), isActive: active });
      notify('success', active ? 'Question brought back' : 'Question retired');
      load();
    } catch (e) {
      notify('error', e.message || 'Could not change the question');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <ClipboardList className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Intake Questions</h1>
                <p className="text-gray-600">
                  The medical history a doctor takes before a patient's first prescription
                </p>
              </div>
            </div>
            {can('EDIT') && (
              <button
                onClick={() => setModal({ open: true, question: null })}
                className="flex items-center space-x-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
              >
                <Plus className="h-5 w-5" />
                <span>Add Question</span>
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-5 mb-6">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-gray-300 focus:ring-emerald-500"
            />
            <span>Show retired questions</span>
          </label>
        </div>

        {questions === null ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
          </div>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <ClipboardList className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-500">No intake questions</h3>
            <p className="text-gray-400 text-sm mt-1">
              Add the first one, and it appears on every new patient's intake form.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl">
            <TablePager paging={paging} noun="question" filteredFrom={questions.length} />
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-semibold text-gray-600 uppercase">
                  <th className="px-6 py-3">Question</th>
                  <th className="px-4 py-3">Section</th>
                  <th className="px-4 py-3">Age groups</th>
                  <th className="px-4 py-3">Answer</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paging.pageRows.map((q) => (
                  <tr key={q.id} className={q.isActive ? '' : 'bg-gray-50/60'}>
                    <td className="px-6 py-3">
                      <p className="urdu-text text-base text-gray-900" dir="rtl" style={{ lineHeight: 2.2 }}>
                        {q.textUrdu}
                      </p>
                      <p className="text-sm text-gray-600">
                        {q.labelEnglish}
                        {q.isRequired ? <span className="text-red-500"> *</span> : null}
                        {!q.isActive && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-600">
                            Retired
                          </span>
                        )}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {SECTION_LABEL[q.section] || q.section}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {bandsOf(q).length === BANDS.length ? (
                          <span className="text-sm text-gray-600">Everyone</span>
                        ) : (
                          bandsOf(q).map((b) => (
                            <span
                              key={b}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100 capitalize"
                            >
                              {BAND_LABEL[b] || b}
                            </span>
                          ))
                        )}
                        {q.genderScope && q.genderScope !== 'ALL' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-100">
                            {q.genderScope === 'FEMALE' ? 'Women only' : 'Men only'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <div>{ANSWER_TYPE_LABEL[q.answerType] || q.answerType}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {q.isSerious && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-100">
                            Serious
                          </span>
                        )}
                        {q.alertType && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-100">
                            Alert: {ALERT_TYPE_LABEL[q.alertType] || q.alertType}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {can('EDIT') && (
                          <button
                            onClick={() => setModal({ open: true, question: q })}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {can('EDIT') && (
                          <button
                            onClick={() => setActive(q, !q.isActive)}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                            title={q.isActive ? 'Retire' : 'Bring back'}
                          >
                            {q.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <TablePager paging={paging} noun="question" filteredFrom={questions.length} position="bottom" />
          </div>
        )}
      </div>

      {modal.open && (
        <QuestionFormModal
          existing={modal.question}
          onClose={() => setModal({ open: false, question: null })}
          onSaved={() => { setModal({ open: false, question: null }); load(); }}
        />
      )}
    </div>
  );
};

export default IntakeQuestionsManagement;
